import React, { useCallback, useEffect, useRef, useState } from "react";
import { useFormContext } from "react-hook-form";
import { FieldErrorMessage, LightDarkText, LightText } from "../ui/Text";
import { AllowedFileType, FILE_VALIDATION_CONFIGS, validateFile } from "@/utils/helper/fileUploadValidation";
import axios from "axios";
import { getUploadUrls } from "@/services/common/docs.services";
import { toastErrorMessage, toastSuccessMessage } from "@/utils/helper/myToast";
import { Center, Col } from "../css";
import { Paperclip } from "lucide-react";
import { AbsoluteModal } from "../ui/Modal";
import ReactCrop, { Crop, PixelCrop } from "react-image-crop";
import { Button } from "@/components/ui/button";
import { getCroppedImg } from "@/utils/helper/image";
import { Image } from "../ui/Image";

const initialCrop: Crop = {
  unit: "px",
  width: 150,
  height: 150,
  x: 0,
  y: 0,
};
interface MESSAGE {
  success: string;
  error: string;
}
interface UploadFileInputProps {
  label: string;
  field: string;
  defaultValue?: string;
  placeholder?: string;
  disabled?: boolean;
  uploadKey: string;
  message: MESSAGE;
  isFreeCrop?: boolean;
  validation: {
    required: boolean;
    allowedFileType: AllowedFileType;
  };
}

const UploadFileInput: React.FC<UploadFileInputProps> = ({
  field,
  disabled = false,
  validation,
  uploadKey,
  defaultValue,
  label,
  message,
  isFreeCrop = false,
}) => {
  const {
    register,
    setError,
    setValue,
    trigger,
    watch,
    formState: { errors },
  } = useFormContext();
  const fieldValue = watch(field);
  const previewUrl = fieldValue?.previewUrl;

  const [uploadProgress, setUploadProgress] = useState(0);
  const [showCropModal, setShowCropModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [crop, setCrop] = useState<Crop>(initialCrop);
  const [isLoading, setIsLoading] = useState(false);
  const isUploading = uploadProgress > 0 && uploadProgress < 100;
  const errorMessage = errors[field]?.message as string;
  const imgRef = useRef<any>(null);

  const handleUploadFile = async () => {
    if (!selectedFile) return;
    // Validate file
    let croppedFile: any;
    let previewUrl = "";

    if (selectedFile && imgRef.current) {
      try {
        setIsLoading(true);
        croppedFile = await getCroppedImg(imgRef.current, crop as PixelCrop, selectedFile.name);
        setSelectedFile(croppedFile);
        const reader = new FileReader();
        reader.onloadend = () => {
          previewUrl = reader.result as string;
        };
        reader.readAsDataURL(croppedFile);
      } catch (error) {
        setIsLoading(false);
        console.error("Error cropping image:", error);
      }
    }
    try {
      const response: any = await getUploadUrls({
        fileName: croppedFile.name,
        fileType: croppedFile.type,
        uploadKey,
      });

      const { preSignedUrl, objectKey } = response.data?.data || {};

      await axios.put(preSignedUrl, croppedFile, {
        headers: {
          "Content-Type": croppedFile.type,
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(((progressEvent.loaded || 0) * 100) / (progressEvent.total || 1));
          setUploadProgress(percentCompleted);
        },
      });
      const fieldValue =
        field === "authorizedDoc"
          ? { path: objectKey, name: selectedFile?.name, previewUrl: previewUrl }
          : {
              value: objectKey,
              previewUrl: previewUrl,
            };

      setValue(field, fieldValue, { shouldDirty: true, shouldTouch: true });
      trigger(field);
      toastSuccessMessage(message?.success || "File uploaded successfully!");
    } catch (error) {
      console.error("Error uploading file:", error);
      toastErrorMessage(message?.error || "Failed to upload file.");
      setError(field, { type: "custom", message: "Failed to upload file" });
    } finally {
      setUploadProgress(0);
      setIsLoading(false);
      setShowCropModal(false);
    }
  };

  const handleOnChangeFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file: any = e.target.files?.[0];
    e.target.value = "";
    if (file) {
      const validationResult = validateFile(file, "image");
      if (validationResult) return toastErrorMessage(validationResult);
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setValue(field, {
          value: defaultValue || "",
          previewUrl: reader.result as string,
        });
      };
      reader.readAsDataURL(file);
      setShowCropModal(true);
    }
  };

  const getRegisterOptions = () => {
    if (disabled) return {};
    return {
      required: validation?.required ? "This field is required." : false,
    };
  };

  const handleCrop = (c: any) => {
    if (imgRef.current) {
      const imgWidth = imgRef.current.width;
      const imgHeight = imgRef.current.height;
      // For images >= 150x150, restrict crop to 150x150
      if (imgWidth >= 150 && imgHeight >= 150) {
        let height: any, width: any;

        if (c.height >= 150) {
          height = c.height;
        }
        if (c.width >= 150) {
          width = c.width;
        }
        setCrop((prev) => {
          return {
            ...c,
            width: width ? width : prev?.width,
            height: height ? height : prev?.height,
          };
        });
      } else {
        // For smaller images, crop to full image
        setCrop({
          unit: "px",
          width: c.width > 10 ? c.height : imgWidth,
          height: c.height > 10 ? c.height : imgHeight,
          x: 0,
          y: 0,
        });
      }
    }
  };
  const centerCrop = useCallback((mediaWidth: number, mediaHeight: number) => {
    // If image is smaller than 150px in either dimension, crop to full image'
    if (mediaWidth < 150 || mediaHeight < 150) {
      setCrop({
        unit: "px",
        width: mediaWidth,
        height: mediaHeight,
        x: 0,
        y: 0,
      });
    } else {
      const x = (mediaWidth - 150) / 2;
      const y = (mediaHeight - 150) / 2;
      setCrop({
        unit: "px",
        width: 150,
        height: 150,
        x: x,
        y: y,
      });
    }
  }, []);

  const handleCloseModal = () => {
    setValue(
      field,
      defaultValue
        ? {
            value: "",
            previewUrl: null,
          }
        : undefined
    );
    setSelectedFile(null);
    setShowCropModal(false);
  };

  const onImageLoad = useCallback(() => centerCrop(imgRef.current.width, imgRef.current.height), [centerCrop]);

  useEffect(() => {
    if (!fieldValue) {
      setValue(field, defaultValue);
    }
  }, [defaultValue]);

  // Remove selected file and reset state
  const handleRemove = () => {
    setValue(
      field,
      field === "authorizedDoc"
        ? undefined
        : {
            value: "",
            previewUrl: "",
          },
      { shouldDirty: true, shouldTouch: true }
    );
    setSelectedFile(null);
  };

  const handleBrowseClick = () => {
    const fileInput = document.getElementById(`upload-file-${field}`);
    if (fileInput) {
      fileInput.click();
    }
  };

  return (
    <div className="relative" key={field}>
      <div className="flex items-center">
        <input type="hidden" {...register(field, getRegisterOptions())} />
        <input
          type="file"
          id={`upload-file-${field}`}
          onChange={handleOnChangeFile}
          style={{ display: "none" }}
          disabled={isUploading}
          accept={FILE_VALIDATION_CONFIGS[validation.allowedFileType as keyof typeof FILE_VALIDATION_CONFIGS].allowedTypes.join(",")}
        />

        <label className="cursor-pointer" htmlFor={`upload-file-${field}`}>
          <Center
            className={`bg-text-primary text-white transition-all active:bg-brand-dark hover:bg-brand-secondary active:ring active:ring-brand-shade active:ring-offset-1 px-4 py-2.5 text-13 rounded focus:outline-none duration-200 flex items-center justify-center relative min-w-[72px] min-h-8 leading-normal font-normal ${
              disabled ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            <Paperclip color="white" size={16} className="mr-2" /> Attach
          </Center>
        </label>
      </div>

      <div className="my-2">{previewUrl && <Image src={previewUrl} alt="" className=" rounded max-h-28" />}</div>
      {errorMessage && <FieldErrorMessage text={errorMessage} />}
      {showCropModal && (
        <AbsoluteModal onClose={handleCloseModal} title={`Crop ${label}`} type="side" showCloseIcon>
          <div className="border-text-primary border rounded-llg border-dashed min-h-40 bg-brand-light max-h-[calc(100vh-100px)] overflow-y-auto mx-5 my-5">
            {previewUrl ? (
              <div className="flex flex-col gap-4 m-8 justify-center items-center">
                <LightDarkText text="To crop, drag the highlighted area" className=" !text-text-default" />
                <ReactCrop
                  crop={selectedFile ? crop : undefined}
                  onChange={handleCrop}
                  aspect={isFreeCrop ? undefined : 1}
                  className="rounded"
                  keepSelection={false}
                >
                  {previewUrl && <img src={previewUrl} ref={imgRef} alt="Preview" className="rounded" onLoad={onImageLoad} />}
                </ReactCrop>

                <Button variant="link" className="text-13 text-text-primary !p-1 " onClick={onImageLoad}>
                  Reset crop area
                </Button>

                <div className=" flex items-center gap-4">
                  <Button onClick={handleRemove} variant="outline" className=" !text-text-default !text-13" disabled={isLoading}>
                    Remove
                  </Button>
                  <Button onClick={handleUploadFile} disabled={isLoading}>
                    {isLoading ? "Uploading..." : "Upload"}
                  </Button>
                </div>
              </div>
            ) : (
              <Col className="py-4 px-25 gap-y-2 items-center">
                <LightDarkText text="Upload your file" className="pb-2 border-b-[1px] border-text-light3" />
                <LightDarkText text="Drag and drop your attachments here" />
                <LightText text="Your file needs to be in JPEG, PNG format with a size of 5MB." className="!text-10" />
                <Button disabled={isLoading} onClick={handleBrowseClick}>
                  Browse attachments
                </Button>
              </Col>
            )}
          </div>
        </AbsoluteModal>
      )}
    </div>
  );
};

export default UploadFileInput;
